import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { NewsItem, NewsService } from "api/generated/cms";
import { CMS_QUERY_OPTIONS } from "./cmsQueryOptions";
import { CmsNewsArticle, toNewsFeed } from "./newsFeed";
import { useGetCmsIssuers } from "./useGetCmsIssuers";
import "./cmsClient";

interface UseGetCmsNewsFeedResult {
  loading: boolean;
  error: Error | null;
  data: CmsNewsArticle[] | undefined;
}

const fetchNews = (): Promise<NewsItem[]> => NewsService.getApiV1News({});

/**
 * Every issuer's news, and Adecla's own, as one feed.
 *
 * The news endpoint names each item's issuer only by FA external id, so the
 * issuer list — usually already cached by the issuers page — is read alongside
 * it for the id, name and logo the feed links and filters by.
 */
export const useGetCmsNewsFeed = (): UseGetCmsNewsFeedResult => {
  const news = useQuery<NewsItem[], Error>(
    ["cms", "news"],
    fetchNews,
    CMS_QUERY_OPTIONS
  );
  const issuers = useGetCmsIssuers();

  const data = useMemo(
    () =>
      news.data && issuers.data
        ? toNewsFeed(news.data, issuers.data)
        : undefined,
    [news.data, issuers.data]
  );

  return {
    loading: news.isInitialLoading || issuers.loading,
    error: news.error ?? issuers.error ?? null,
    data,
  };
};
